import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useScrollAnimation, fadeInUpVariants, staggerContainerVariants } from '../../hooks/useScrollAnimation';
import { useAppleAuth } from '../../hooks/useAppleAuth';
import './ChefCarousel.css';
import token from '../../assets/dollar.png';
import appicon from '../../assets/appIcon.png';
import ChefVideoModal from '../ChefVideoModal/ChefVideoModal';

// Данные о шеф-поварах
const chefs = [
    {
      id: 'gray',
      type: 'gray',
      image: require('../../assets/banner/2.png'),
      name: 'Feliks',
      video: require('../../assets/purple1.mp4'),
    },
    {
      id: 'pink',
      type: 'pink',
      image: require('../../assets/banner/1.png'),
      name: 'Viki',
      video: require('../../assets/pink.mp4'),
    },
    {
      id: 'yellow',
      type: 'yellow',
      image: require('../../assets/banner/3.png'),
      name: 'Donald',
      video: require('../../assets/yellow.mp4'),
    },
    {
      id: 'red',
      type: 'red',
      image: require('../../assets/banner/4.png'),
      name: 'Jong',
      video: require('../../assets/red.mp4'),
    },
];

const ChefCarousel = () => {
  const [containerRef, controls] = useScrollAnimation();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedChef, setSelectedChef] = useState(chefs[0]);
  const carouselRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  
  // Изменяем начальное состояние isVisible на true
  const [isVisible, setIsVisible] = useState(true);
  
  // Добавляем состояние для видео модального окна
  const [videoModalChef, setVideoModalChef] = useState(null);
  
  // Используем хук авторизации Apple
  const { login, loading: authLoading, error: authError, user } = useAppleAuth();
  
  // Создаем ref для каждого видео
  const videoRefs = useRef(chefs.map(() => React.createRef()));
  
  const selectChef = (index) => {
    setCurrentIndex(index);
    setSelectedChef(chefs[index]);
    
    // Улучшенный скролл до выбранной карточки
    if (carouselRef.current) {
      const carouselContainer = carouselRef.current.parentElement;
      const cardWidth = 280; // Ширина карточки из CSS
      const gap = 20; // Отступ между карточками из CSS
      
      // Вычисляем позицию для центрирования карточки
      const containerWidth = carouselContainer.offsetWidth;
      const centerOffset = (containerWidth - cardWidth) / 2;
      
      // Индивидуальные настройки скролла для каждого шефа
      let scrollAdjustment = 80; // Значение по умолчанию
      
      // Задаем индивидуальные смещения для каждого шефа
      switch (index) {
        case 0:
          scrollAdjustment = 70;
          break;
        case 1:
          scrollAdjustment = 85;
          break;
        case 2:
          scrollAdjustment = 140;
          break;
        case 3:
          scrollAdjustment = 75;
          break;
        default:
          scrollAdjustment = 80;
      }
      
      const scrollPosition = index * (cardWidth + gap) - centerOffset - scrollAdjustment;
      
      carouselContainer.scrollTo({
        left: Math.max(0, scrollPosition),
        behavior: 'smooth'
      });
    }
    
    // Управление воспроизведением видео только после выбора
    videoRefs.current.forEach((ref, i) => {
      if (ref.current) {
        if (i === index) {
          ref.current.currentTime = 0; // Сбрасываем время видео
          setTimeout(() => {
            ref.current.play().catch(err => console.error('Video playback error:', err));
          }, 100); // Небольшая задержка перед запуском видео
        } else {
          ref.current.pause();
        }
      }
    });
  };
  
  // Функция для показа видео модального окна
  const showVideoModal = (chef) => {
    setVideoModalChef(chef);
  };
  
  // Функция для закрытия видео модального окна
  const closeVideoModal = () => {
    setVideoModalChef(null);
  };
  
  const handleStartClick = async () => {
    try {
      setIsLoading(true);
      
      // Вызываем реальную авторизацию через Apple
      const { success, error, user } = await login();
      
      if (success) {
        console.log('Успешный вход через Apple');
        setIsSuccess(true);
        
        // Если у пользователя есть токены, показываем их количество
        const tokenAmount = user?.tokens || 126;
        setModalMessage(`Congratulations! You have successfully registered! Claim your ${tokenAmount} bonus points in the app.`);
        setShowModal(true);
      } else {
        // При ошибке входа
        setIsSuccess(false);
        setModalMessage('Get your 96 tokens in the application.');
        setShowModal(true);
      }
    } catch (error) {
      console.error('Ошибка регистрации:', error);
      setIsSuccess(false);
      setModalMessage('Get your 96 tokens in the application.');
      setShowModal(true);
    } finally {
      setIsLoading(false);
    }
  };
  
  const closeModal = () => {
    setShowModal(false);
  };
  
  // Убираем задержку появления элементов
  useEffect(() => {
    // Предзагрузка всех изображений для снижения лагов
    chefs.forEach(chef => {
      const img = new Image();
      img.src = chef.image;
    });
    
    // Убираем таймер и сразу устанавливаем isVisible = true
    setIsVisible(true);
  }, []);

  // Добавляем обработку ресайза для корректного отображения карточек
  useEffect(() => {
    const handleResize = () => {
      // При изменении размера окна обновляем скролл, чтобы выбранная карточка оставалась по центру
      selectChef(currentIndex);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [currentIndex]);

  const ChefCard = ({ chef, index, currentIndex }) => (
    <motion.div
      key={chef.id}
      className={`chef-card-item ${index === currentIndex ? 'selected' : ''}`}
      onClick={() => {
        selectChef(index);
        showVideoModal(chef); // Показываем видео модальное окно при клике
      }}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
    >
      <div className="chef-card">
        <img 
          src={chef.image} 
          alt={chef.name} 
          className="chef-image-in-card"
        />
        
        <video 
          ref={videoRefs.current[index]}
          src={chef.video}
          className={`chef-video-in-card ${index === currentIndex ? 'visible' : 'hidden'}`}
          muted 
          playsInline
          loop
          preload="metadata"
          style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover' }}
        />
        
        <div className="chef-info" style={{ position: 'absolute', bottom:-40, left: 0, width: '100%', zIndex: 10 }}>
          <h3 className="chef-name">{chef.name}</h3>
          
          {/* {index === currentIndex && (
            <motion.button 
              className="start-button"
              onClick={(e) => {
                e.stopPropagation();
                handleStartClick();
              }}
              disabled={isLoading || authLoading}
              initial={{ opacity: 1 }}
              animate={{ opacity: 1 }}
              style={{ marginTop: '10px' }}
            >
              {isLoading || authLoading ? 'LOADING...' : 'START'}
            </motion.button>
          )} */}
        </div>
        
        <div className="card-overlay"></div>
        <div className="card-glow"></div>
      </div>
    </motion.div>
  );

  return (
    <motion.div 
      className="carousel-container"
      ref={containerRef}
      initial="visible"
      animate="visible"
    >
      {/* Подключаем видео модальное окно */}
      <AnimatePresence>
        {videoModalChef && (
          <ChefVideoModal 
            chef={videoModalChef} 
            onClose={closeVideoModal} 
          />
        )}
      </AnimatePresence>
      
      {/* Добавляем градиентный фон */}
      <div className="chef-background">
        <div className="gradient-orb orb-1"></div>
        <div className="gradient-orb orb-2"></div>
        <div className="gradient-orb orb-3"></div>
        <div className="gradient-overlay"></div>
      </div>
      
      {/* Модифицируем анимацию появления всего контента */}
      <motion.div 
        className="content-wrapper"
        initial={{ opacity: 1, y: 0 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="background-blob"></div>
        
        <motion.h2 
          className="carousel-title"
          initial={{ opacity: 1 }}
          animate={{ opacity: 1 }}
        >
          Select your Chef
        </motion.h2>
        
        <motion.div 
          className="carousel-outer-wrapper"
          initial={{ opacity: 1 }}
          animate={{ opacity: 1 }}
        >
          <div className="carousel-wrapper">
            <div className="carousel-slider" ref={carouselRef}>
              {chefs.map((chef, index) => (
                <ChefCard key={chef.id} chef={chef} index={index} currentIndex={currentIndex} />
              ))}
            </div>
          </div>
          
          {/* Кнопки навигации */}
          <div className="carousel-navigation">
            <button 
              className={`nav-button prev ${currentIndex === 0 ? 'hidden' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                currentIndex > 0 && selectChef(currentIndex - 1);
              }}
            >
              ←
            </button>
            <button 
              className={`nav-button next ${currentIndex === chefs.length - 1 ? 'hidden' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                currentIndex < chefs.length - 1 && selectChef(currentIndex + 1);
              }}
            >
              →
            </button>
          </div>
        </motion.div>
        
        <div className="carousel-controls">
          {chefs.map((chef, index) => (
            <button 
              key={chef.id}
              className={`control-dot ${index === currentIndex ? 'active' : ''}`}
              onClick={() => selectChef(index)}
            />
          ))}
        </div>

        {(isLoading || authLoading) && (
          <div className="loading-overlay">
            <div className="spinner"></div>
          </div>
        )}
        
        {/* Летающие частицы для фона */}
        <div className="light-spot top-left"></div>
        <div className="light-spot bottom-right"></div>
        
        {[1, 2, 3, 4, 5].map((_, index) => (
          <div key={index} className={`floating-particle particle-${index+1}`}></div>
        ))}
        
        {/* Модифицируем модальное окно, оставляя только анимацию появления */}
        {showModal && (
          <div className="modal-overlay">
            <motion.div 
              className="modal-content glass-card"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ 
                type: "spring", 
                stiffness: 80,
                damping: 15
              }}
            >
              <div className="glow-background"></div>
              
              <motion.div 
                className="app-icon"
                initial={{ opacity: 1, scale: 1, rotateZ: 0 }}
                animate={{ opacity: 1, scale: 1, rotateZ: 0 }}
                whileHover={{
                  scale: 1.05,
                  rotateZ: 5,
                  transition: {
                    duration: 0.3,
                    yoyo: Infinity
                  }
                }}
              ></motion.div>
              
              <motion.h2 
                className="prompt-title"
                initial={{ opacity: 1, y: 0 }}
                animate={{ opacity: 1, y: 0 }}
              >
                {isSuccess ? 'Exclusive Offer' : 'Special Offer'}
              </motion.h2>
              
              <motion.p 
                className="prompt-description"
                initial={{ opacity: 1, y: 0 }}
                animate={{ opacity: 1, y: 0 }}
              >
                {isSuccess ? (
                  <>Get <span className="tokens-highlight">126 <img src={token} alt="token" className="token-icon" /></span> when you download our app today. Create amazing meals with {selectedChef.name}!</>
                ) : (
                  <>Get <span className="tokens-highlight">96 <img src={token} alt="token" className="token-icon" /></span> when you download our app today. Create amazing meals from your fridge leftovers!</>
                )}
              </motion.p>
              
              <motion.a
                 target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => {
                  e.preventDefault(); // чтобы не переходило мгновенно
                  if (typeof window !== 'undefined' && window.gtag_report_conversion) {
                    window.gtag_report_conversion('https://apps.apple.com/us/app/chefkats-ai-snap-cook/id6739225693');
                  } else {
                    // fallback — если функция не сработала
                    window.location.href = 'https://apps.apple.com/us/app/chefkats-ai-snap-cook/id6739225693';
                  }
                }}
                className="download-button"
                initial={{ opacity: 1, scale: 1 }}
                animate={{ opacity: 1, scale: 1 }}
                whileHover={{ 
                  scale: 1.05,
                  boxShadow: "0 15px 30px rgba(10, 132, 255, 0.5)"
                }}
                whileTap={{ scale: 0.97 }}
              >
                <div className="apple-icon"></div>
                Download  
              </motion.a>
              
              <motion.button 
                className="close-modal-button"
                onClick={closeModal}
                initial={{ opacity: 1 }}
                animate={{ opacity: 1 }}
                whileHover={{ backgroundColor: "rgba(255, 255, 255, 0.1)" }}
              >
                Close
              </motion.button>
              
              {[1, 2, 3].map((_, index) => (
                <motion.div 
                  key={index}
                  className={`floating-element element-${index+1}`}
                  animate={{ 
                    x: [0, 20, -30, 0],
                    y: [0, -15, 25, 0] 
                  }}
                  transition={{ 
                    duration: 15 + index * 5, 
                    repeat: Infinity, 
                    repeatType: "reverse",
                    ease: "easeInOut" 
                  }}
                ></motion.div>
              ))}
            </motion.div>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};

export default ChefCarousel; 